/**
 * "Your Live plan is paused until 12 Nov 2026. [Resume now] Stay paused ×" —
 * the prompt on sign-in while a pause is under way (`payments.md` § Pausing a
 * plan, mock approved 2026-10-13).
 *
 * A row in the flow beside `UpdateBar`, for its reason: it covers no control.
 * Asked once per sign-in (and on a change of account); **Stay paused** hides it
 * until the next sign-in, and the plan screen still offers Resume.
 */

import { useEffect, useState } from "react";

import { plans, resumePlan } from "../api/billing";
import { formatRenewal, type Held } from "../lib/billing";
import { reread as rereadEntitlements } from "../lib/entitlements";
import { onAuthChanged } from "../lib/session";

export function PauseBar() {
  const [held, setHeld] = useState<Held | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const off = onAuthChanged((uid) => {
      setHeld(null);
      setError(null);
      if (!uid) return;
      void plans().then((answer) => {
        const current = answer?.current ?? null;
        if (alive && current?.pause?.state === "active") setHeld(current);
      });
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  const pause = held?.pause;
  if (!held || pause?.state !== "active") return <div role="status" />;

  const resume = async () => {
    setBusy(true);
    setError(null);
    try {
      await resumePlan();
      void rereadEntitlements();
      setHeld(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="status" style={BAR}>
      <span>
        Your {held.name} plan is paused until {formatRenewal(pause.endsAt)}.
      </span>
      <button type="button" style={RESUME} disabled={busy} onClick={() => void resume()}>
        Resume now
      </button>
      {error && <span style={{ color: "var(--qf-negative)" }}>{error}</span>}
      <button type="button" style={STAY} disabled={busy} onClick={() => setHeld(null)}>
        Stay paused <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

const BAR: React.CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "0.25rem 0.7rem",
  padding: "0.3rem 0.8rem",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  borderBottom: "1px solid var(--qf-border)",
  fontSize: "0.78rem",
  lineHeight: 1.3,
};

const RESUME: React.CSSProperties = {
  padding: "0.15rem 0.65rem",
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  border: "none",
  borderRadius: 4,
  font: "inherit",
  fontWeight: 600,
  cursor: "pointer",
};

const STAY: React.CSSProperties = {
  marginLeft: "auto",
  padding: "0.15rem 0.3rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  border: "none",
  font: "inherit",
  cursor: "pointer",
};
